import { Button } from "@/components/ui/button";
import { Video, Eye, Heart, Zap } from "lucide-react";
import { Link } from "react-router-dom";


const benefits = [
  { icon: Eye, title: "Visual Accountability", description: "Seeing someone else work keeps you on task" },
  { icon: Heart, title: "Emotional Support", description: "Feel companionship without pressure to interact" },
  { icon: Zap, title: "Mirror Effect", description: "Watching focus triggers your own focus response" },
];

export const StudyWithMeSection = () => {
  return (
    <section className="py-24 relative overflow-hidden" aria-labelledby="study-with-me-title">
      <div className="absolute inset-0 bg-gradient-to-b from-background via-accent/5 to-background" aria-hidden="true" />
      <div className="absolute top-1/3 right-1/4 w-[400px] h-[400px] bg-secondary/5 rounded-full blur-[100px]" aria-hidden="true" />

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 animate-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 border border-secondary/20 mb-6">
              <Video className="w-4 h-4 text-secondary" aria-hidden="true" />
              <span className="text-sm font-semibold text-secondary">STUDY WITH ME</span>
            </div>

            <h1 id="study-with-me-title" className="font-display text-4xl sm:text-5xl font-bold">
              <span className="text-gradient">Study With Me</span>
            </h1>
          </div>




          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <article className="space-y-6 animate-fade-in">
              <p className="text-lg text-muted-foreground leading-relaxed">
                Can't find a study buddy? "Study With Me" sessions let you study alongside a host who broadcasts their session. Research shows viewers use these videos to personalise their ambience, create peer pressure and gain emotional support.
              </p>

              <p className="text-muted-foreground leading-relaxed">
                Search interest in "Study With Me" videos has tripled in the last five years, reflecting a growing desire for virtual body doubling. By watching someone else focus, you're more likely to mirror that behaviour and stay on task.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105" asChild>
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">Join a Session</a>
                </Button>
                <Button size="lg" variant="outline" className="border-2 border-border hover:bg-card font-semibold px-8 py-6 text-lg transition-all hover:scale-105" asChild>
                  <Link to="/study-with-me/">Learn More</Link>
                </Button>
              </div>
            </article>

            {/* Right: Benefits */}
            <aside className="space-y-4 animate-fade-in" style={{ animationDelay: '0.2s' }} aria-label="Study With Me benefits">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div
                    key={benefit.title}
                    className="group p-6 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-secondary/40 hover:bg-card/80 transition-all duration-300 animate-slide-in"
                    style={{ animationDelay: `${0.3 + index * 0.1}s` }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-secondary/20 to-primary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Icon className="w-6 h-6 text-secondary" aria-hidden="true" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">{benefit.title}</h3>
                        <p className="text-sm text-muted-foreground">{benefit.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </aside>
          </div>
        </div>
      </div>
    </section>
  );
};